import { useEffect, useRef } from 'react';

const SCROLL_THRESHOLD = 15;   // px before direction triggers hide/show
const TOP_THRESHOLD = 100;     // px from top — always show chrome
const BOTTOM_THRESHOLD = 100;  // px from bottom — restore chrome (ready to type)
const MOBILE_BREAKPOINT = 768; // px — only activate on mobile

/**
 * Scroll-to-hide bottom chrome (input-area + nav) for mobile chat.
 *
 * Adds/removes `.bottom-chrome-hidden` on `.app-shell`.
 * CSS handles the actual transition on .input-area and .nav-container.
 *
 * Guards:
 *   - Mobile only (< 768px)
 *   - Never hides when AI is generating (`sending === true`)
 *   - Always shows when near top (scrollTop < 100)
 *   - Always shows when near bottom (ready-to-type zone)
 *
 * @param {{ containerRef: React.RefObject, sending: boolean }} opts
 */
export function useChatScrollHide({ containerRef, sending }) {
  const sendingRef = useRef(sending);
  sendingRef.current = sending;

  // Restore chrome immediately when generation starts
  useEffect(() => {
    if (sending) {
      document.querySelector('.app-shell')?.classList.remove('bottom-chrome-hidden');
    }
  }, [sending]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const shell = document.querySelector('.app-shell');
    if (!shell) return;

    const state = { lastScrollY: 0, cumulative: 0, lastDir: null };
    let ticking = false;

    const setHidden = (hidden) => {
      shell.classList.toggle('bottom-chrome-hidden', hidden);
    };

    const process = () => {
      ticking = false;

      // Only activate on mobile
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        setHidden(false);
        return;
      }

      // Never hide during AI generation (stop button must stay accessible)
      if (sendingRef.current) {
        setHidden(false);
        return;
      }

      const scrollY = el.scrollTop;
      const delta = scrollY - state.lastScrollY;
      state.lastScrollY = scrollY;

      // Always show when near top
      if (scrollY < TOP_THRESHOLD) {
        setHidden(false);
        state.cumulative = 0;
        state.lastDir = null;
        return;
      }

      // Always show when near bottom (user is about to type)
      const distFromBottom = el.scrollHeight - el.clientHeight - scrollY;
      if (distFromBottom < BOTTOM_THRESHOLD) {
        setHidden(false);
        state.cumulative = 0;
        state.lastDir = null;
        return;
      }

      const dir = delta > 0 ? 'down' : delta < 0 ? 'up' : null;
      if (!dir) return;

      // Reset cumulative on direction change
      if (dir !== state.lastDir) {
        state.cumulative = 0;
        state.lastDir = dir;
      }

      state.cumulative += Math.abs(delta);
      if (state.cumulative >= SCROLL_THRESHOLD) {
        setHidden(dir === 'down');
        state.cumulative = 0;
      }
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(process);
        ticking = true;
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      // Always restore chrome on unmount (tab switch etc.)
      shell.classList.remove('bottom-chrome-hidden');
    };
  }, [containerRef]);
}
